// Configuración → Personas: the owner custom-field admin for CONTACTS (CDP L4b).
// A tenant owner/admin defines what their contact ficha carries.
//
// This screen used to manage both entities behind a switcher. Since CP3 L it
// manages contacts ALONE: a lead field is only ever meaningful inside a process,
// so the lead half moved to Procesos → Datos del proceso, where the process that
// captures the data is the one that decides it carries it. The API still serves
// both entities (GET /me/fields?entity=…); only the web reorganized.
//
// Managed presets (seeded per vertical) can be shown/hidden but not relabeled or
// deleted — and a managed LISTA has its OPTIONS edited here: the vertical owns
// the name and the type, the tenant owns the list. Composed on the design system.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { OptionsSourceSelect, SourcedOptionsList } from './FieldOptionsSource.js'
import {
  DATA_TYPE_LABELS,
  type FieldDataType,
  type FieldDefinition,
  type FieldEntityType,
  type FieldOptionsSource,
  createField,
  deleteField,
  listFields,
  slugifyKey,
  updateField,
} from './fields-data.js'
import {
  Button,
  Chip,
  DataTable,
  type DataTableColumn,
  DropdownMenu,
  FormField,
  Input,
  type MenuItem,
  Modal,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

/** Esta pantalla es de CONTACTOS y de nada más. La constante existe para que la
 *  entidad se lea una vez y no quede escrita a mano en cada llamada. */
const ENTITY: FieldEntityType = 'contact'

const TYPE_OPTIONS: { value: FieldDataType; label: string }[] = (
  Object.keys(DATA_TYPE_LABELS) as FieldDataType[]
).map((t) => ({ value: t, label: DATA_TYPE_LABELS[t] }))

function DotsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="size-4">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  )
}

/** Parse a textarea of options (one per line, or comma-separated). */
function parseOptions(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function FieldsSection(): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['fields', ENTITY],
    queryFn: () => listFields(ENTITY),
  })

  // Create modal.
  const [createOpen, setCreateOpen] = useState(false)
  const [cLabel, setCLabel] = useState('')
  const [cKey, setCKey] = useState('')
  const [cKeyTouched, setCKeyTouched] = useState(false)
  const [cType, setCType] = useState<FieldDataType>('text')
  const [cOptions, setCOptions] = useState('')
  // De dónde saldrán sus opciones (D21-1): 'lista' es lo de siempre.
  const [cSource, setCSource] = useState<FieldOptionsSource>('lista')
  // Edit modal (unmanaged only).
  const [editTarget, setEditTarget] = useState<FieldDefinition | null>(null)
  const [eLabel, setELabel] = useState('')
  const [eOptions, setEOptions] = useState('')
  const [eSource, setESource] = useState<FieldOptionsSource>('lista')
  // Delete confirm.
  const [deleteTarget, setDeleteTarget] = useState<FieldDefinition | null>(null)
  const [busy, setBusy] = useState(false)

  async function refetch(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['fields', ENTITY] })
  }

  function resetCreate(): void {
    setCreateOpen(false)
    setCLabel('')
    setCKey('')
    setCKeyTouched(false)
    setCType('text')
    setCOptions('')
    setCSource('lista')
  }

  /** Una lista que TOMA sus opciones de una fuente (D21-1). Solo un dato de
   *  tipo lista puede tenerla, así que la pregunta desaparece con el tipo. */
  const cSourced = cType === 'enum' && cSource !== 'lista'

  async function submitCreate(): Promise<void> {
    const label = cLabel.trim()
    const key = (cKeyTouched ? cKey : slugifyKey(cLabel)).trim()
    if (!label || !key) {
      toast.error('Faltan datos', { description: 'El nombre y la clave son obligatorios.' })
      return
    }
    if (cType === 'enum' && !cSourced && parseOptions(cOptions).length === 0) {
      toast.error('Faltan opciones', { description: 'Una lista necesita al menos una opción.' })
      return
    }
    setBusy(true)
    try {
      await createField({
        entity_type: ENTITY,
        key,
        label,
        data_type: cType,
        // Con fuente no van opciones: la definición no guarda copia de una
        // lista viva, y mandarlas sería un 400 en palabras.
        ...(cType === 'enum' && !cSourced ? { options: parseOptions(cOptions) } : {}),
        ...(cSourced ? { options_source: cSource } : {}),
      })
      await refetch()
      toast.ok('Campo creado')
      resetCreate()
    } catch (err) {
      // The API names the actual problem (a duplicate key, a reserved one, a
      // permission refusal). Guessing "quizás ya existe" for all of them sent
      // people to edit the one field that was fine.
      toast.error('No se pudo crear', {
        description: apiErrorMessage(err, 'Revisa la clave (quizás ya existe).'),
      })
    } finally {
      setBusy(false)
    }
  }

  function openEdit(f: FieldDefinition): void {
    setEditTarget(f)
    setELabel(f.label)
    // La lista que se enseña es la que el API mandó, que en un dato con fuente
    // es la VIVA. Sirve de punto de partida si se decide volver a escribirla a
    // mano: volver a 'lista' exige traer opciones en el mismo guardado.
    setEOptions((f.options ?? []).join('\n'))
    setESource(f.options_source)
  }

  const eSourced = editTarget?.data_type === 'enum' && eSource !== 'lista'

  async function submitEdit(): Promise<void> {
    if (!editTarget) return
    if (editTarget.data_type === 'enum' && !eSourced && parseOptions(eOptions).length === 0) {
      toast.error('Faltan opciones', { description: 'Una lista necesita al menos una opción.' })
      return
    }
    setBusy(true)
    try {
      await updateField(editTarget.id, {
        // Un campo del sistema conserva su NOMBRE y su TIPO (los nombran los
        // flujos, las reglas y los requisitos de etapa); mandar el label lo
        // haría rebotar con 409.
        ...(editTarget.managed ? {} : { label: eLabel.trim() }),
        // Con fuente no viajan opciones (el API responde 409 y dice dónde se
        // cambian); sin fuente viajan siempre, incluso al volver de una, que es
        // justo cuando el API las exige.
        ...(editTarget.data_type === 'enum' && !eSourced
          ? { options: parseOptions(eOptions) }
          : {}),
        ...(editTarget.data_type === 'enum' ? { options_source: eSource } : {}),
      })
      await refetch()
      toast.ok('Campo actualizado')
      setEditTarget(null)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo actualizar'))
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(f: FieldDefinition): Promise<void> {
    try {
      await updateField(f.id, { active: !f.active })
      await refetch()
      toast.ok(f.active ? 'Campo ocultado' : 'Campo activado')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo cambiar'))
    }
  }

  async function submitDelete(): Promise<void> {
    if (!deleteTarget) return
    setBusy(true)
    try {
      await deleteField(deleteTarget.id)
      await refetch()
      toast.ok('Campo eliminado')
      setDeleteTarget(null)
    } catch (err) {
      // e.g. "El campo está en uso" — the reason deleting was refused is the
      // only thing that tells them what to do next.
      toast.error(apiErrorMessage(err, 'No se pudo eliminar'))
    } finally {
      setBusy(false)
    }
  }

  function menuFor(f: FieldDefinition): MenuItem[] {
    const items: MenuItem[] = []
    if (!f.managed) items.push({ label: 'Editar', onSelect: () => openEdit(f) })
    // Un campo del sistema de tipo lista: el nombre es del giro, la LISTA es
    // del espacio. La clínica que atiende cuatro especialidades que nadie
    // adivinó tiene que poder decirlo.
    else if (f.data_type === 'enum')
      items.push({ label: 'Editar opciones', onSelect: () => openEdit(f) })
    items.push({
      label: f.active ? 'Ocultar' : 'Activar',
      onSelect: () => void toggleActive(f),
    })
    if (!f.managed)
      items.push({ label: 'Eliminar', danger: true, onSelect: () => setDeleteTarget(f) })
    return items
  }

  const columns: DataTableColumn<FieldDefinition>[] = useMemo(
    () => [
      {
        key: 'label',
        label: 'Campo',
        render: (f) => (
          <span className="min-w-0">
            <span className="block font-semibold text-text">{f.label}</span>
            <span className="block truncate font-mono text-[11px] text-text-3">{f.key}</span>
          </span>
        ),
      },
      {
        key: 'type',
        label: 'Tipo',
        render: (f) => <span className="text-text-2">{DATA_TYPE_LABELS[f.data_type]}</span>,
      },
      {
        key: 'origin',
        label: 'Origen',
        render: (f) =>
          f.managed ? (
            <Chip tone="draft">Del sistema</Chip>
          ) : (
            <Chip tone="info">Personalizado</Chip>
          ),
      },
      {
        key: 'status',
        label: 'Estado',
        render: (f) => (
          <Chip tone={f.active ? 'ok' : 'draft'} dot>
            {f.active ? 'Activo' : 'Oculto'}
          </Chip>
        ),
      },
      {
        key: 'menu',
        label: '',
        align: 'right',
        render: (f) => (
          <DropdownMenu
            trigger={
              <button
                type="button"
                aria-label={`Acciones para ${f.label}`}
                className="inline-flex size-8 items-center justify-center rounded-lg text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
              >
                <DotsIcon />
              </button>
            }
            items={menuFor(f)}
          />
        ),
      },
    ],
    // menuFor closes over the current handlers; the table content never changes
    // entity any more, so the list has nothing left to key on.
    [],
  )

  const rows = data ?? []
  // Mirror the name as a suggested key until the user edits it; blank while the
  // name is empty (don't show a bare "campo_" stub).
  const keyPreview = cKeyTouched ? cKey : cLabel.trim() ? slugifyKey(cLabel) : ''

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-xl">
          <p className="text-[12.5px] leading-relaxed text-text-2" data-testid="fields-intro">
            Los datos de tus personas (contactos): lo que ves y editas en la ficha de cada contacto.
          </p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-text-3">
            Los campos del sistema vienen con tu giro: puedes ocultarlos, pero no eliminarlos. Los
            datos de tus prospectos se configuran en cada proceso, en Procesos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            Nuevo campo
          </Button>
        </div>
      </div>

      {isError ? (
        <QueryError message="No pudimos cargar tus campos." onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="py-10">
          <Spinner label="Cargando campos…" />
        </div>
      ) : (
        <DataTable
          caption="Campos de tus personas"
          columns={columns}
          rows={rows}
          empty="Aún no tienes campos para tus personas."
        />
      )}

      {/* Create */}
      <Modal
        open={createOpen}
        onClose={resetCreate}
        title="Nuevo campo"
        subtitle="Se agrega a la ficha de cada persona."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={resetCreate} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitCreate()}
            >
              {busy ? 'Creando…' : 'Crear campo'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <FormField label="Nombre" htmlFor="field-label">
            <Input
              id="field-label"
              placeholder="Aseguradora"
              value={cLabel}
              onChange={(e) => setCLabel(e.target.value)}
            />
          </FormField>
          <FormField
            label="Clave"
            htmlFor="field-key"
            helper="Identificador interno. Se genera del nombre; cámbialo solo si lo necesitas."
          >
            <Input
              id="field-key"
              className="font-mono"
              placeholder="aseguradora"
              value={keyPreview}
              onChange={(e) => {
                setCKeyTouched(true)
                setCKey(e.target.value)
              }}
            />
          </FormField>
          <FormField label="Tipo" htmlFor="field-type">
            <Select
              options={TYPE_OPTIONS}
              value={cType}
              onChange={(e) => setCType(e.target.value as FieldDataType)}
            />
          </FormField>
          {cType === 'enum' && (
            <OptionsSourceSelect id="field-options-source" value={cSource} onChange={setCSource} />
          )}
          {cType === 'enum' &&
            (cSource !== 'lista' ? (
              // La lista todavía no existe (el dato no se ha creado), pero la
              // fuente sí: lo que se enseña es de dónde va a salir.
              <SourcedOptionsList source={cSource} options={null} />
            ) : (
              <FormField label="Opciones" htmlFor="field-options" helper="Una por línea.">
                <Textarea
                  id="field-options"
                  placeholder={'GNP\nAXA\nMetLife'}
                  value={cOptions}
                  onChange={(e) => setCOptions(e.target.value)}
                />
              </FormField>
            ))}
        </div>
      </Modal>

      {/* Edit — completo para un campo propio, solo las opciones para uno del
          sistema de tipo lista. */}
      <Modal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        title={editTarget?.managed ? 'Editar opciones' : 'Editar campo'}
        subtitle={editTarget ? editTarget.key : ''}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditTarget(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button size="sm" className="ml-auto" disabled={busy} onClick={() => void submitEdit()}>
              Guardar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          {editTarget?.managed ? (
            <p className="text-[12.5px] leading-relaxed text-text-2">
              Los campos del sistema conservan su nombre y su tipo: los usan tus flujos y la ficha
              de cada contacto. Sus opciones son tuyas, cámbialas cuando quieras.
            </p>
          ) : (
            <FormField label="Nombre" htmlFor="edit-field-label">
              <Input
                id="edit-field-label"
                value={eLabel}
                onChange={(e) => setELabel(e.target.value)}
              />
            </FormField>
          )}
          {editTarget?.data_type === 'enum' && (
            <OptionsSourceSelect
              id="edit-field-options-source"
              value={eSource}
              onChange={setESource}
            />
          )}
          {editTarget?.data_type === 'enum' &&
            (eSource !== 'lista' ? (
              <SourcedOptionsList
                source={eSource}
                // La lista viva solo se puede enseñar si es la que el API ya
                // resolvió: si en este diálogo se acaba de ELEGIR otra fuente,
                // todavía no hay lista que enseñar, y la de la fuente anterior
                // no es la de esta.
                options={eSource === editTarget.options_source ? (editTarget.options ?? []) : null}
              />
            ) : (
              <FormField label="Opciones" htmlFor="edit-field-options" helper="Una por línea.">
                <Textarea
                  id="edit-field-options"
                  value={eOptions}
                  onChange={(e) => setEOptions(e.target.value)}
                />
              </FormField>
            ))}
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar campo"
        subtitle={deleteTarget ? deleteTarget.label : ''}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitDelete()}
            >
              {busy ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </>
        }
      >
        <p className="text-[13px] leading-relaxed text-text-2">
          {deleteTarget
            ? `Se borra "${deleteTarget.label}" y todos los valores guardados en las fichas. Esta acción no se puede deshacer.`
            : ''}
        </p>
      </Modal>
    </section>
  )
}
